import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./UserProfileImage.css";

const BASE_URL =
  window.location.hostname === "localhost"
   ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

function UserProfileImage({ onImageClick }) {
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [profilePic, setProfilePic] = useState("");
  const [bio, setBio] = useState(""); // 🔥 BIO STATE 
  const [tempBio, setTempBio] = useState(""); // 
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [savingBio, setSavingBio] = useState(false); // Bio save loading
  const [toast, setToast] = useState(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [pulseRing, setPulseRing] = useState(false);
  const fileRef = useRef(null);

  const userId = localStorage.getItem("userId");

  const showToast = (msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // FETCH PROFILE IMAGE + BIO
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/profile/${userId}`);
        setProfilePic(res.data.profilePic || "");
        setBio(res.data.bio || ""); // 🔥 BIO FETCH
        setTempBio(res.data.bio || "");
      } catch (err) {
        console.error(err);
      }
    };
    if (userId) fetchProfile();
  }, [userId]);

  // CLEANUP PREVIEW URL
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showToast("Only image files allowed ❌", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast("Image must be under 5MB ❌", "error");
      return;
    }

    if (preview) URL.revokeObjectURL(preview);

    setImage(file);
    setPreview(URL.createObjectURL(file));
    setImgLoaded(false);

    setPulseRing(true);
    setTimeout(() => setPulseRing(false), 1500);
  };

  const handleUpload = async () => {
    if (!image) {
      showToast("Select an image first", "error");
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append("profile", image);
    formData.append("userId", userId);

    try {
      await axios.post(`${BASE_URL}/api/profile/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const res = await axios.get(`${BASE_URL}/api/profile/${userId}`);
      setProfilePic(res.data.profilePic || "");

      if (preview) URL.revokeObjectURL(preview);
      setPreview(null);
      setImage(null);

      if (fileRef.current) fileRef.current.value = "";
      setImgLoaded(false);

      showToast("Profile photo updated ✅", "success");
      setPulseRing(true);
      setTimeout(() => setPulseRing(false), 2000);

    } catch (err) {
      console.error(err);
      showToast("Upload failed. Try again ❌", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!profilePic) {
      showToast("No photo to remove", "error");
      return;
    }

    setRemoving(true);

    try {
      await axios.delete(`${BASE_URL}/api/profile/remove/${userId}`);

      setProfilePic("");

      if (preview) URL.revokeObjectURL(preview);
      setPreview(null);
      setImage(null);
      setImgLoaded(false);
      if (fileRef.current) fileRef.current.value = "";

      showToast("Photo removed ✅", "success");

    } catch (err) {
      console.error(err);
      showToast("Remove failed ❌", "error");
    } finally {
      setRemoving(false);
    }
  };

  // 🔥 BIO SAVE FUNCTION
  const handleSaveBio = async () => {
    if (tempBio.trim().length > 200) {
      showToast("Bio must be under 200 characters ❌", "error");
      return;
    }

    setSavingBio(true);

    try {
      await axios.put(`${BASE_URL}/api/profile/bio/${userId}`, {
        bio: tempBio.trim()
      });

      setBio(tempBio.trim());
      showToast("Bio updated ✅", "success");

    } catch (err) {
      console.error(err);
      showToast("Bio save failed ❌", "error");
    } finally {
      setSavingBio(false);
    }
  };

  const displaySrc =
    preview ||
    profilePic ||
    "https://cdn-icons-png.flaticon.com/512/149/149071.png";

  return (
    <div className="upi-wrapper">

      {toast && (
        <div className={`upi-toast upi-toast-${toast.type}`}>
          {toast.msg}
        </div>
      )}

      {/* Compact layout: photo + Upload/Remove on the LEFT, Bio on the RIGHT */}
      <div className="upi-layout">
        <div className="upi-photo-col">
          {image && (
            <div className="upi-status-badge upi-status-pending">
              ⏳ Pending upload
            </div>
          )}

          <div className={`upi-avatar-outer ${pulseRing? "upi-pulse" : ""}`}>
            <div className="upi-glow-ring" />
            {!imgLoaded && <div className="upi-skeleton" />}

            <img
              src={displaySrc}
              alt="profile"
              className={`upi-avatar-img ${
                imgLoaded? "upi-img-visible" : "upi-img-hidden"
              } ${onImageClick ? "upi-avatar-clickable" : ""}`}
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgLoaded(true)}
              onClick={onImageClick}
              title={onImageClick ? "View status" : undefined}
            />

            {uploading && (
              <div className="upi-upload-overlay">
                <div className="upi-upload-spinner" />
              </div>
            )}

            <div
              className="upi-camera-overlay"
              onClick={(e) => {
                // Keep "change photo" independent of the view-status click.
                e.stopPropagation();
                fileRef.current?.click();
              }}
              title="Change photo"
            >
              📷
            </div>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            style={{ display: "none" }}
          />

          {image && <p className="upi-filename">📎 {image.name}</p>}

          <p className="upi-hint">
            Click photo to change · Max 5MB · Square crop works best
          </p>
        </div>

        {/* Upload + Remove side by side, equal width */}
        <div className="upi-btn-group">
          <button
            className="upi-btn upi-btn-upload"
            onClick={handleUpload}
            disabled={uploading ||!image}
          >
            {uploading? (
              <>
                <span className="upi-spinner" /> Uploading...
              </>
            ) : (
              <>⬆️ Upload</>
            )}
          </button>

          <button
            className="upi-btn upi-btn-remove"
            onClick={handleRemove}
            disabled={removing || (!profilePic &&!preview)}
          >
            {removing? (
              <>
                <span className="upi-spinner" /> Removing...
              </>
            ) : (
              <>🗑 Remove</>
            )}
          </button>
        </div>

        {/* 🔥 BIO SECTION */}
        <div className="upi-bio-section">
          <h3 className="upi-bio-title">📝 Bio</h3>
          <textarea
            className="upi-bio-input"
            placeholder="Tell something about yourself..."
            value={tempBio}
            onChange={(e) => setTempBio(e.target.value)}
            maxLength={200}
            rows={3}
          />
          <div className="upi-bio-footer">
            <span className="upi-bio-count">{tempBio.length}/200</span>
            <button
              className="upi-btn upi-btn-save-bio"
              onClick={handleSaveBio}
              disabled={savingBio || tempBio === bio}
            >
              {savingBio? (
                <>
                  <span className="upi-spinner" /> Saving...
                </>
              ) : (
                <>💾 Save Bio</>
              )}
            </button>
          </div>
        </div>
      </div>

    </div>
  );
}

export default UserProfileImage;